import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, of, tap } from 'rxjs';
import { Store } from './store';
import { toVoid } from '../utils/void';
import { Permission, SessionDto } from '../models/session.model';
import { SessionProvider } from './session.provider';

// 'unknown' until the first load() answers — the route guard waits for it
export type SessionStatus = 'unknown' | 'signedIn' | 'signedOut';

export type SignInError = 'invalid' | 'locked' | 'failed';

export interface SessionState {
  status: SessionStatus;
  session: SessionDto | undefined;
  signingIn: boolean;
  signInError: SignInError | undefined;
}

const INITIAL_STATE: SessionState = {
  status: 'unknown',
  session: undefined,
  signingIn: false,
  signInError: undefined
};

// The first app-wide store: who is signed in, read by the guard, the shell and the sign-in page.
// Never destroyed — signing out reloads the document instead (see signOut)
@Injectable({ providedIn: 'root' })
export class SessionStore extends Store<SessionState> {
  constructor(private provider: SessionProvider) {
    super(INITIAL_STATE);
  }

  // The app-side half of the permission gate: pages ask "may I?" by permission, never by role name (0056)
  can(permission: Permission): boolean {
    return this.state.session?.permissions.includes(permission) ?? false;
  }

  // 401 is the normal "signed out" answer, not an error; so is any other failure here —
  // the sign-in page is where an unreachable API gets reported
  load(): Observable<void> {
    return this.provider.getSession().pipe(
      tap(session => this.update(state => ({ ...state, status: 'signedIn', session }))),
      catchError(() => {
        this.update(state => ({ ...state, status: 'signedOut', session: undefined }));
        return of(undefined);
      }),
      toVoid()
    );
  }

  // The set-password page signs in without the sign-in call: the API answered it with the same SessionDto (0057)
  accept(session: SessionDto): void {
    this.update(state => ({ ...state, status: 'signedIn', session, signInError: undefined }));
  }

  signIn(email: string, password: string, rememberMe: boolean): Observable<void> {
    this.update(state => ({ ...state, signingIn: true, signInError: undefined }));

    return this.provider.signIn(email, password, rememberMe).pipe(
      tap(session => this.update(state => ({ ...state, status: 'signedIn', session, signingIn: false }))),
      catchError((error: unknown) => {
        this.update(state => ({ ...state, signingIn: false, signInError: toSignInError(error) }));
        return of(undefined);
      }),
      toVoid()
    );
  }

  // Every page store is root-provided and keeps its state, so a full reload is the one way
  // to be sure nothing of the previous user survives in memory
  signOut(): Observable<void> {
    return this.provider.signOut().pipe(
      // a session already gone on the server is signed out all the same
      catchError(() => of(undefined)),
      tap(() => document.location.assign('/sign-in')),
      toVoid()
    );
  }
}

// 401 covers an unknown e-mail too: the API answers both alike on purpose (0054)
function toSignInError(error: unknown): SignInError {
  if (error instanceof HttpErrorResponse && error.status === 401) {
    return 'invalid';
  }

  if (error instanceof HttpErrorResponse && error.status === 429) {
    return 'locked';
  }

  return 'failed';
}
